import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  styled,
  Tabs,
  Tab,
  Autocomplete
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Close as CloseIcon } from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterIcon from '@mui/icons-material/FilterList';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useToast } from '../../hooks/useToast';
import {
  appendCashReconLedgerCoaQueryParams,
  fetchCashReconLedgerScope,
  unwrapGlTransactionPage,
} from './cashReconLedgerScope';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDrawerHeaderFlushSx,
  harmonateDrawerBodySx,
  harmonateDrawerAccordionGroupSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
  harmonateDrawerAddIconSx,
  harmonateDrawerEditIconSx,
  harmonateDrawerDeleteIconSx,
  harmonateDialogCloseIconSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateDialogContentSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateUploadOutlinedSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateDataGridSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const UploadArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${ht.line}`,
  borderRadius: '8px',
  padding: '30px 20px',
  textAlign: 'center',
  backgroundColor: ht.listBg,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: ht.green,
    backgroundColor: ht.secondaryTab,
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

const GLTransactionDrawer = ({ open, onClose }) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.CASH_RECONCILIATION);
  const { showSuccess, showError, showInfo } = useToast();

  // Tab panel helper functions
  const a11yProps = (index) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };

  const CustomTabPanel = ({ children, value, index, ...other }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box>{children}</Box>}
      </div>
    );
  };

  // Accordion state
  const [expanded, setExpanded] = useState('transactions');

  // State for GL transactions data
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState(null);
  const [txPaginationModel, setTxPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [txRowCount, setTxRowCount] = useState(0);
  const txFetchSeq = useRef(0);
  const [selectedTransactionRow, setSelectedTransactionRow] = useState(null);

  // State for filters
  const [dateRangeFilter, setDateRangeFilter] = useState({ start: '', end: '' });
  const [propertyGroupFilter, setPropertyGroupFilter] = useState('ALL');
  const [propertyIdFilter, setPropertyIdFilter] = useState('ALL');
  const [glAccountFilter, setGlAccountFilter] = useState('ALL');
  const [reconciliationStatusFilter, setReconciliationStatusFilter] = useState('ALL');
  const [showFilters, setShowFilters] = useState(false);

  // State for property dropdown
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);

  // State for property groups dropdown
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [propertyGroupsLoading, setPropertyGroupsLoading] = useState(false);

  // State for ledger accounts dropdown
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  const [ledgerAccountsLoading, setLedgerAccountsLoading] = useState(false);

  // State for upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedPropertyApi, setSelectedPropertyApi] = useState('');
  const [loadingPropertyApis, setLoadingPropertyApis] = useState(false);
  const [propertyApis, setPropertyApis] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeUploadTab, setActiveUploadTab] = useState(0); // 0 for Excel, 1 for Snowflake

  // State for Snowflake data
  const [snowflakeData, setSnowflakeData] = useState([]);
  const [snowflakeLoading, setSnowflakeLoading] = useState(false);
  const [snowflakeError, setSnowflakeError] = useState(null);
  const [snowflakeFromDate, setSnowflakeFromDate] = useState('');
  const [snowflakeToDate, setSnowflakeToDate] = useState('');

  // State for templates and preview
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState({
    fileName: '',
    templateName: '',
    excelFields: [],
    templateFields: [],
    mappings: {},
    excelData: []
  });
  const [customMappings, setCustomMappings] = useState({});

  // State for API ingestion
  const [apiIngestionLoading, setApiIngestionLoading] = useState(false);

  // State for Add GL Transaction Dialog
  const [addGLTransactionDialogOpen, setAddGLTransactionDialogOpen] = useState(false);
  const [newGLTransaction, setNewGLTransaction] = useState({
    ledgerAccountId: '',
    transactionDate: '',
    referenceNo: '',
    description: '',
    // debitAmount: null,
    // creditAmount: null,
    amountType: 'debit', // 'debit' or 'credit'
    amount: null,
    propertyId: '',
    glAccountNo: '',
    transactionType: '',
    sourceSystem: 'Manual'
  });
  const [addGLTransactionLoading, setAddGLTransactionLoading] = useState(false);
  const [addGLTransactionError, setAddGLTransactionError] = useState(null);

  // State for Edit GL Transaction Dialog
  const [editGLTransactionDialogOpen, setEditGLTransactionDialogOpen] = useState(false);
  const [editingGLTransaction, setEditingGLTransaction] = useState(null);
  const [editGLTransactionLoading, setEditGLTransactionLoading] = useState(false);
  const [editGLTransactionError, setEditGLTransactionError] = useState(null);

  // State for Delete GL Transaction Dialog
  const [deleteGLTransactionDialogOpen, setDeleteGLTransactionDialogOpen] = useState(false);
  const [deletingGLTransaction, setDeletingGLTransaction] = useState(null);
  const [deleteGLTransactionLoading, setDeleteGLTransactionLoading] = useState(false);
  const [deleteGLTransactionError, setDeleteGLTransactionError] = useState(null);

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setTransactions([]);
      setTransactionsError(null);
      setSelectedTransactionRow(null);
      setDateRangeFilter({ start: '', end: '' });
      setPropertyGroupFilter('ALL');
      setPropertyIdFilter('ALL');
      setGlAccountFilter('ALL');
      setReconciliationStatusFilter('ALL');
      setShowFilters(false);
      setUploadDialogOpen(false);
      setSelectedPropertyApi('');
      setUploadFile(null);
      setUploadProgress(0);
      setUploading(false);
      setUploadError(null);
      setIsDragging(false);
      setActiveUploadTab(0); // Reset to Excel tab
      setSnowflakeData([]);
      setSnowflakeError(null);
      setSnowflakeLoading(false);
      setSnowflakeFromDate('');
      setSnowflakeToDate('');
      setSelectedTemplate('');
      setTemplates([]);
      setCustomMappings({});
      setPreviewOpen(false);
      setPreviewData({
        fileName: '',
        templateName: '',
        excelFields: [],
        templateFields: [],
        mappings: {},
        excelData: []
      });
      setPropertyApis([]);
      setPropertyGroups([]);
      setExpanded('transactions');
      setAddGLTransactionDialogOpen(false);
      setNewGLTransaction({
        ledgerAccountId: '',
        transactionDate: '',
        referenceNo: '',
        description: '',
        // debitAmount: null,
        // creditAmount: null,
        amountType: 'debit',
        amount: null,
        propertyId: '',
        glAccountNo: '',
        sourceSystem: 'Manual'
      });
      setEditGLTransactionDialogOpen(false);
      setEditingGLTransaction(null);
      setDeleteGLTransactionDialogOpen(false);
      setDeletingGLTransaction(null);
      setAddGLTransactionError(null);
      setEditGLTransactionError(null);
      setDeleteGLTransactionError(null);
    }
  }, [open]);
  // Keep filter dropdown properties aligned with selected property group.
  useEffect(() => {
    if (!open) return;

    // Also reset propertyIdFilter when group changes
    setPropertyIdFilter('ALL');

    if (propertyGroupFilter === 'ALL') {
      // Reuse already-fetched property APIs to avoid duplicate by-tenant calls.
      setProperties(propertyApis);
      return;
    }

    fetchProperties();
  }, [open, propertyGroupFilter, propertyApis]);

  // Fetch property groups for dropdown
  const fetchPropertyGroups = async () => {
    setPropertyGroupsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/property-groups/by-tenant?tenant_id=${tenantId}`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch property groups: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (Array.isArray(data)) {
        setPropertyGroups(data);
      } else {
        setPropertyGroups([]);
      }
    } catch (err) {
      console.error('Error fetching property groups:', err);
      setPropertyGroups([]);
    } finally {
      setPropertyGroupsLoading(false);
    }
  };

  // Fetch properties for dropdown
  // const fetchProperties = async () => {
  //   setPropertiesLoading(true);
  //   try {
  //     const token = localStorage.getItem('token');
  //     const tenantId = localStorage.getItem('tenant_id');

  //     if (!token) throw new Error('No authentication token found');
  //     if (!tenantId) throw new Error('No tenant ID found');

  //     const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
  //     const url = `${baseUrl}conductor/cash-recon/properties/by-tenant?tenant_id=${tenantId}`;

  //     const resp = await fetch(url, {
  //       method: 'GET',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //     });

  //     if (!resp.ok) {
  //       throw new Error(`Failed to fetch properties: ${resp.status} ${resp.statusText}`);
  //     }

  //     const data = await resp.json();

  //     if (Array.isArray(data)) {
  //       setProperties(data);
  //     } else {
  //       setProperties([]);
  //     }
  //   } catch (err) {
  //     console.error('Error fetching properties:', err);
  //     setProperties([]);
  //   } finally {
  //     setPropertiesLoading(false);
  //   }
  // };
  const fetchProperties = async () => {
    setPropertiesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      const url = `${baseUrl}conductor/cash-recon/properties/by-tenant-with-filters?tenant_id=${tenantId}&group_id=${propertyGroupFilter}`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch properties: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (Array.isArray(data)) {
        setProperties(data);
      } else {
        setProperties([]);
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setProperties([]);
    } finally {
      setPropertiesLoading(false);
    }
  };

  // Fetch ledger accounts for dropdown
  const fetchLedgerAccounts = async (propertyId) => {
    if (!propertyId) {
      setLedgerAccounts([]);
      return;
    }

    setLedgerAccountsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      // const url = `${baseUrl}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property?tenant_id=${tenantId}&property_id=${propertyId}`;
      const url = await appendCashReconLedgerCoaQueryParams(
        `${baseUrl}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property?tenant_id=${tenantId}`
      , { baseUrl, tenantId, token });

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch ledger accounts: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (Array.isArray(data)) {
        setLedgerAccounts(data);
      } else {
        setLedgerAccounts([]);
      }
    } catch (err) {
      console.error('Error fetching ledger accounts:', err);
      setLedgerAccounts([]);
    } finally {
      setLedgerAccountsLoading(false);
    }
  };

  // Fetch one page of GL transactions. The API never returns the full table.
  const fetchTransactions = async (page = 0, pageSize = 100) => {
    const seq = ++txFetchSeq.current;
    setTransactionsLoading(true);
    setTransactionsError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      // Build URL with filters
      const params = new URLSearchParams();
      params.append('tenant_id', tenantId);
      params.append('limit', String(pageSize));
      params.append('offset', String(page * pageSize));

      if (dateRangeFilter.start) {
        params.append('start_date', dateRangeFilter.start);
      }

      if (dateRangeFilter.end) {
        params.append('end_date', dateRangeFilter.end);
      }

      if (propertyGroupFilter !== 'ALL') {
        params.append('property_group_id', propertyGroupFilter);
      }

      if (propertyIdFilter !== 'ALL') {
        params.append('property_id', propertyIdFilter);
      }

      if (glAccountFilter !== 'ALL') {
        params.append('gl_account_name', glAccountFilter);
      }

      if (reconciliationStatusFilter !== 'ALL') {
        params.append('recon_status', reconciliationStatusFilter);
      }

      const url = await appendCashReconLedgerCoaQueryParams(
        `${baseUrl}conductor/cash-recon/gl-transactions/by-tenant-filter?${params.toString()}`
      , { baseUrl, tenantId, token });

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('GL Transactions API Response Status:', resp.status);
      console.log('GL Transactions API URL:', url);

      if (!resp.ok) {
        const errorText = await resp.text();
        console.log('GL Transactions API Error:', errorText);

        // If API doesn't exist yet, return mock data
        if (resp.status === 404) {
          console.log('GL Transactions API not found, using mock data');
          const mockData = [
            {
              id: 1,
              transactionDate: '2024-01-15',
              glAccount: '1001-Cash',
              referenceNo: 'REF001',
              description: 'Cash receipt from customer',
              debit: 1000.00,
              credit: 0.00,
              propertyId: 'PROP001',
              propertyGroup: 'Group A',
              sourceIntegration: 'Manual',
              reconciliationStatus: 'Pending',
              isReadOnly: false
            },
            {
              id: 2,
              transactionDate: '2024-01-16',
              glAccount: '2001-Accounts Payable',
              referenceNo: 'REF002',
              description: 'Payment to vendor',
              debit: 0.00,
              credit: 500.00,
              propertyId: 'PROP002',
              propertyGroup: 'Group B',
              sourceIntegration: 'API',
              reconciliationStatus: 'Approved',
              isReadOnly: true
            }
          ];
          setTransactions(mockData);
          setTxRowCount(mockData.length);
          setTransactionsLoading(false);
          return;
        }
        throw new Error(`Failed to fetch GL transactions: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();
      if (seq !== txFetchSeq.current) return;

      const pageResult = unwrapGlTransactionPage(data);
      if (pageResult) {
        const processedData = pageResult.content.map((item, index) => ({
          id: item.id || index + 1,
          transactionDate: item.transactionDate || '',
          glAccount: item.glAccountName || '',
          referenceNo: item.referenceNo || '',
          description: item.description || '',
          debit: item.debitAmount || 0,
          credit: item.creditAmount || 0,
          propertyId: item.propertyId || '',
          propertyGroup: item.propertyGroup || '',
          sourceIntegration: (item.sourceSystem || 'Manual').toUpperCase(),
          reconciliationStatus: item.reconStatus || 'UNMATCHED',
          isReadOnly: item.reconStatus === 'AUTO_MATCHED' || item.reconStatus === 'MANUAL_MATCHED' || item.approvalStatus === 'APPROVED',
          rawData: item,
        }));
        setTransactions(processedData);
        setTxRowCount(pageResult.total);
      } else {
        setTransactions([]);
        setTxRowCount(0);
      }
      setTransactionsLoading(false);
    } catch (err) {
      if (seq !== txFetchSeq.current) return;
      console.error('Error fetching GL transactions:', err);
      setTransactionsError(err.message || 'Failed to load GL transactions');
      setTransactions([]);
      setTxRowCount(0);
      setTransactionsLoading(false);
    }
  };

  // Fetch transactions when drawer opens or filters change
  // useEffect(() => {
  //   if (open && !transactionsLoading) {
  //     fetchTransactions();
  //     // fetchProperties();
  //     fetchPropertyGroups();
  //     fetchPropertyApis();
  //   }
  // }, [open, dateRangeFilter, propertyGroupFilter, propertyIdFilter, glAccountFilter, reconciliationStatusFilter]);
  useEffect(() => {
    if (open) {
      fetchPropertyGroups();
      fetchPropertyApis();
    }
  }, [open]);

  // Refetch page 0 when the drawer opens or the filters change.
  useEffect(() => {
    if (!open) return;
    setTxPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    fetchTransactions(0, txPaginationModel.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dateRangeFilter, propertyGroupFilter, propertyIdFilter, glAccountFilter, reconciliationStatusFilter]);

  // Fetch ledger accounts when property changes in Add dialog
  useEffect(() => {
    if (newGLTransaction.propertyId) {
      fetchLedgerAccounts(newGLTransaction.propertyId);
    } else {
      setLedgerAccounts([]);
    }
  }, [newGLTransaction.propertyId]);

  // Fetch ledger accounts when Edit dialog opens (so dropdown has options like Property)
  useEffect(() => {
    if (editGLTransactionDialogOpen && editingGLTransaction?.propertyId) {
      fetchLedgerAccounts(editingGLTransaction.propertyId);
    } else if (editGLTransactionDialogOpen && editingGLTransaction?.rawData?.propertyId) {
      fetchLedgerAccounts(editingGLTransaction.rawData.propertyId);
    }
  }, [editGLTransactionDialogOpen, editingGLTransaction?.propertyId, editingGLTransaction?.rawData?.propertyId]);

  // Fetch Properties (using correct API endpoint)
  const fetchPropertyApis = async () => {
    setLoadingPropertyApis(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      const { systemType, glHeaderId } = await fetchCashReconLedgerScope({ baseUrl, tenantId, token });

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${localStorage.getItem('user_id')}`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch properties: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (data && Array.isArray(data)) {
        const processedData = data.map((item, index) => ({
          id: item.propertyId || index + 1,
          propertyId: item.propertyId || '',
          propertyName: item.propertyName || '',
          propertyCode: item.propertyCode || '',
          propertyGroupId: item.propertyGroupId || '',
          companyId: item.companyId || '',
          addressLine1: item.addressLine1 || '',
          city: item.city || '',
          state: item.state || '',
          country: item.country || '',
          postalCode: item.postalCode || '',
          baseCurrency: item.baseCurrency || 'USD',
          defaultBankAccountId: item.defaultBankAccountId || null,
          cashReconEnabled: item.cashReconEnabled !== false,
          status: item.status || 'ACTIVE',
          deleted: item.deleted || false,
          createdAt: item.createdAt || '',
          updatedAt: item.updatedAt || '',
        }));
        setPropertyApis(processedData);
      } else {
        setPropertyApis([]);
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setPropertyApis([]);
    } finally {
      setLoadingPropertyApis(false);
    }
  };

  // Fetch Snowflake data
  const fetchSnowflakeData = async () => {
    if (!selectedPropertyApi) {
      setSnowflakeData([]);
      setSnowflakeError('Please select a property to load Snowflake data');
      return;
    }

    setSnowflakeLoading(true);
    setSnowflakeError(null);
    setUploadError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_integration');

      if (!token) {
        throw new Error('No authentication token found');
      }
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      // Build API URL with required parameters
      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/cash-recon/gl-transactions?tenant_id=${tenantId}`;

      // Add date filters if provided
      if (snowflakeFromDate) {
        apiUrl += `&start_date=${snowflakeFromDate}`;
      }
      if (snowflakeToDate) {
        apiUrl += `&end_date=${snowflakeToDate}`;
      }

      // Add property name filter
      const selectedProperty = propertyApis.find(property => property.propertyId === selectedPropertyApi);
      if (selectedProperty && selectedProperty.propertyName) {
        apiUrl += `&property_name=${encodeURIComponent(selectedProperty.propertyName)}`;
      }

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Handle API errors
      if (!resp.ok) {
        console.error(`API returned ${resp.status} error`);
        setSnowflakeData([]); // Show empty grid on error
        setSnowflakeError(`Failed to load data: ${resp.status} ${resp.statusText}`);
        return;
      }

      const data = await resp.json();

      if (data.status === 'success' && Array.isArray(data.data)) {
        // Check if API has data
        if (data.data.length > 0) {
          // Map API fields to grid (flat columns + CONTENT + NEW_CONTENT)
          const processedData = data.data.map((item, index) => {
            const content = item.CONTENT || {};
            const newContent = item.NEW_CONTENT || {};
            const description = item.TRANSACTION_DESCRIPTION || newContent.TRANSACTION_DESCRIPTION || newContent.TRANSACTION_MEMO || content.description || '';
            const date = item.POSTING_DATE || newContent.POSTING_DATE || content.entryDate || '';
            const docNo = item.DOCUMENT_NUMBER || item.REFERENCE_NUMBER || newContent.DOCUMENT_NUMBER || newContent.REFERENCE_NUMBER || content['journalEntry.txnNumber'] || content['journalEntry.key'] || '';
            const debit = item.DEBIT ?? newContent.DEBIT ?? (content.txnType === 'debit' ? Number(content.txnAmount) : 0) ?? 0;
            const credit = item.CREDIT ?? newContent.CREDIT ?? newContent.AMOUNT ?? (content.txnType === 'credit' ? Number(content.txnAmount) : 0) ?? 0;
            const glDesc = item.GL_DESCRIPTION || content['glAccount.name'] || '';
            const glCode = item.GL_CODE || content['glAccount.id'] || '';
            const rowId = item.GL_SYSTEM_ID || newContent.GL_SYSTEM_ID || content.id || content.key || index + 1;
            const glSystemTransactionId = item.GL_SYSTEM_TRANSACTION_ID || newContent.GL_SYSTEM_TRANSACTION_ID || rowId;
            return {
              id: rowId,
              glSystemTransactionId,
              description,
              date,
              docNo,
              debit: Number(debit) || 0,
              credit: Number(credit) || 0,
              remarks: `Txn Date: ${date} | Doc_No: ${docNo} | Desc: ${description} | Debits: ${Number(debit) || 0} | Credits: ${Number(credit) || 0}`,
              glDescription: glDesc,
              glCode,
              selected: false,
              isMapped: !!(
                (item.MAPPED_DEBITS && item.MAPPED_DEBITS !== 0) ||
                (item.MAPPED_CREDITS && item.MAPPED_CREDITS !== 0) ||
                item.MAPPED_BANK_IDS ||
                item.MAPPING_SOURCES ||
                item.MAPPING_REFERENCES
              ),
              originalData: item,
              isApiData: true,
            };
          });
          setSnowflakeData(processedData);
        } else {
          // API returned empty data, show empty grid
          setSnowflakeData([]);
        }
      } else {
        throw new Error('Invalid response format from Snowflake API');
      }
    } finally {
      setSnowflakeLoading(false);
    }
  };

  // Fetch templates for GL Transactions
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      // For GL Transactions, we'll use a specific endpoint or filter
      const url = `${baseUrl}conductor/ref-templates/by-document-type?documentTypeId=GL_TRANSACTIONS&tenant_id=${tenantId}`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        // If the endpoint doesn't exist, create default templates
        if (resp.status === 404) {
          const defaultTemplates = [
            {
              id: 'gl_transactions_default',
              name: 'GL Transactions Default',
              description: 'Default template for GL Transactions upload',
              mappings: {
                'TRANSACTION_DATE': { targetColumn: 'transactionDate', dataType: 'DATE', isRequired: true },
                'LEDGER_ACCOUNT_NAME': { targetColumn: 'glAccountName', dataType: 'TEXT', isRequired: true },
                'GL_ACCOUNT': { targetColumn: 'glAccountNo', dataType: 'TEXT', isRequired: false },
                'REFERENCE_NUMBER': { targetColumn: 'referenceNo', dataType: 'TEXT', isRequired: true },
                'DESCRIPTION': { targetColumn: 'description', dataType: 'TEXT', isRequired: true },
                'DEBIT': { targetColumn: 'debitAmount', dataType: 'NUMBER', isRequired: false },
                'CREDIT': { targetColumn: 'creditAmount', dataType: 'NUMBER', isRequired: false }
              }
            }
          ];
          setTemplates(defaultTemplates);
        } else {
          throw new Error(`Failed to fetch templates: ${resp.status} ${resp.statusText}`);
        }
      } else {
        const data = await resp.json();
        const transformedTemplates = Array.isArray(data) ? data.map(template => ({
          id: template.templateId,
          name: template.templateName,
          description: template.description,
          mappings: template.mapping || {}
        })) : [];
        setTemplates(transformedTemplates);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      // Create default templates on error
      const defaultTemplates = [
        {
          id: 'gl_transactions_default',
          name: 'GL Transactions Default',
          description: 'Default template for GL Transactions upload',
          mappings: {
            'TRANSACTION_DATE': { targetColumn: 'transactionDate', dataType: 'DATE', isRequired: true },
            'LEDGER_ACCOUNT_NAME': { targetColumn: 'glAccountName', dataType: 'TEXT', isRequired: true },
            'GL_ACCOUNT': { targetColumn: 'glAccountNo', dataType: 'TEXT', isRequired: false },
            'REFERENCE_NUMBER': { targetColumn: 'referenceNo', dataType: 'TEXT', isRequired: true },
            'DESCRIPTION': { targetColumn: 'description', dataType: 'TEXT', isRequired: true },
            'DEBIT': { targetColumn: 'debitAmount', dataType: 'NUMBER', isRequired: false },
            'CREDIT': { targetColumn: 'creditAmount', dataType: 'NUMBER', isRequired: false }
          }
        }
      ];
      setTemplates(defaultTemplates);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Fetch templates when dialog opens
  useEffect(() => {
    if (uploadDialogOpen) {
      fetchTemplates();
    }
  }, [uploadDialogOpen]);

  // Function to dynamically load XLSX library
  const loadXLSX = async () => {
    try {
      const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs');
      return XLSX;
    } catch (error) {
      console.warn('Failed to load XLSX from CDN, trying local import:', error);
      const XLSX = await import('xlsx');
      return XLSX;
    }
  };

  // Convert value based on data type
  const convertValue = (value, dataType) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    const strValue = String(value).trim();
    const type = (dataType || 'TEXT').toUpperCase();

    switch (type) {
      case 'NUMBER':
      case 'INTEGER':
      case 'FLOAT':
      case 'DECIMAL':
        const numValue = strValue.replace(/[^0-9.-]/g, '');
        return numValue === '' ? null : Number(numValue);
      case 'BOOLEAN':
        const lowerValue = strValue.toLowerCase();
        if (['true', 'yes', 'y', '1', 't'].includes(lowerValue)) return true;
        if (['false', 'no', 'n', '0', 'f'].includes(lowerValue)) return false;
        return Boolean(strValue);
      case 'DATE':
        // First check if it's a number (Excel serial date)
        const numericValue = parseFloat(strValue);
        if (!isNaN(numericValue) && numericValue > 0 && numericValue < 100000) {
          // Convert Excel serial date to JavaScript date
          // Excel epoch starts at 1900-01-01, but Excel incorrectly treats 1900 as a leap year
          // So we need to adjust by 1 day for dates after 1900-02-28
          const excelEpoch = new Date(1900, 0, 1);
          const daysOffset = numericValue - 1; // Adjust for Excel leap year bug
          const jsDate = new Date(excelEpoch.getTime() + (daysOffset * 24 * 60 * 60 * 1000));
          return jsDate.toISOString().split('T')[0];
        }

        const date = new Date(strValue);
        if (isNaN(date.getTime())) {
          // Try to parse various date formats
          try {
            // Handle MM/dd/yyyy format
            if (strValue.includes('/')) {
              const parts = strValue.split('/');
              if (parts.length === 3) {
                const month = parts[0].padStart(2, '0');
                const day = parts[1].padStart(2, '0');
                const year = parts[2];
                return `${year}-${month}-${day}`;
              }
            }
            return strValue;
          } catch {
            return strValue;
          }
        }
        return date.toISOString().split('T')[0];
      case 'TEXT':
      default:
        return strValue.replace(/\s+/g, ' ').trim();
    }
  };

  // Extract fields and data from Excel file
  const extractFieldsFromSheet = async (file) => {
    try {
      const XLSX = await loadXLSX();
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

      if (!jsonData || jsonData.length === 0) {
        return { fields: [], data: [] };
      }

      const headers = jsonData[0].map(h => h ? h.toString().trim() : '').filter(h => h !== '');
      const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== ''));

      const dataRows = rows.map(row => {
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = row[index] || '';
        });
        return obj;
      });

      return { fields: headers, data: dataRows };
    } catch (error) {
      console.error('Error extracting fields from sheet:', error);
      return { fields: [], data: [] };
    }
  };

  // Handle preview button click
  const handlePreviewClick = async () => {
    if (!selectedTemplate || !uploadFile) return;

    try {
      const template = templates.find(t => t.id === selectedTemplate);
      if (!template) {
        throw new Error('Selected template not found');
      }

      const { fields: excelFields, data: excelData } = await extractFieldsFromSheet(uploadFile);

      // Build field mappings
      const fieldMappings = {};
      const templateFields = [];

      if (template.mappings) {
        Object.entries(template.mappings).forEach(([sourceField, mapping]) => {
          if (mapping && mapping.isActive !== false) {
            const matchedField = excelFields.find(field =>
              field.toLowerCase() === sourceField.toLowerCase()
            );

            if (matchedField) {
              fieldMappings[sourceField] = {
                targetColumn: mapping.targetColumn || sourceField,
                dataType: mapping.dataType || 'TEXT',
                isRequired: mapping.isRequired || false,
                excelField: matchedField
              };
              templateFields.push(sourceField);
            }
          }
        });
      }

      // Apply custom mappings
      Object.entries(customMappings).forEach(([excelField, templateField]) => {
        if (!fieldMappings[templateField] && excelFields.includes(excelField)) {
          const mapping = template.mappings?.[templateField];
          fieldMappings[templateField] = {
            targetColumn: mapping?.targetColumn || templateField,
            dataType: mapping?.dataType || 'TEXT',
            isRequired: mapping?.isRequired || false,
            excelField: excelField
          };
          if (!templateFields.includes(templateField)) {
            templateFields.push(templateField);
          }
        }
      });

      // Process excel data
      const processedData = excelData.map(row => {
        const mappedRow = {};
        templateFields.forEach(field => {
          const mapping = fieldMappings[field];
          if (mapping?.excelField && row[mapping.excelField] !== undefined) {
            mappedRow[field] = convertValue(row[mapping.excelField], mapping.dataType);
          }
        });
        return mappedRow;
      });

      setPreviewData({
        fileName: uploadFile.name,
        templateName: template.name || 'Unnamed Template',
        excelFields: excelFields,
        templateFields: templateFields,
        mappings: fieldMappings,
        excelData: processedData
      });

      setPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setUploadError(`Failed to generate preview: ${error.message}`);
    }
  };

  // Handle custom field mapping
  const handleCustomMapping = (excelField, templateField) => {
    setCustomMappings(prev => {
      const newMappings = { ...prev };

      // Remove any previous mapping for this excel field
      Object.keys(newMappings).forEach(key => {
        if (newMappings[key] === templateField) {
          delete newMappings[key];
        }
      });

      // Add new mapping if templateField is provided
      if (templateField && templateField !== '') {
        newMappings[excelField] = templateField;
      } else {
        delete newMappings[excelField];
      }

      return newMappings;
    });
  };

  // Handle preview apply
  const handlePreviewApply = () => {
    setPreviewOpen(false);
    showSuccess('Field mappings applied successfully');
  };

  // Process upload helper function
  const processUpload = async (transactionsData, url, token) => {
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transactionsData),
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`Failed to upload GL transactions: ${resp.status} ${resp.statusText} - ${errorText}`);
      }

      const result = await resp.json();

      // Handle different response formats
      // API returns either an array of transactions or an object with success/data fields
      if (Array.isArray(result)) {
        // Direct array response
        await fetchTransactions();

        const selectedProperty = propertyApis.find(p => p.propertyId === selectedPropertyApi);
        const propertyName = selectedProperty ? selectedProperty.propertyName : selectedPropertyApi;
        showSuccess(`Successfully uploaded ${result.length || transactionsData.length} GL Transactions for ${propertyName}`);
        setUploadDialogOpen(false);
        setUploadFile(null);
        setSelectedPropertyApi('');
        setSelectedTemplate('');
        setCustomMappings({});
        setUploadProgress(0);
      } else if (result.success) {
        // Object response with success field
        await fetchTransactions();

        const selectedProperty = propertyApis.find(p => p.propertyId === selectedPropertyApi);
        const propertyName = selectedProperty ? selectedProperty.propertyName : selectedPropertyApi;
        showSuccess(`Successfully uploaded ${result.data?.length || transactionsData.length} GL Transactions for ${propertyName}`);
        setUploadDialogOpen(false);
        setUploadFile(null);
        setSelectedPropertyApi('');
        setSelectedTemplate('');
        setCustomMappings({});
        setUploadProgress(0);
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (apiError) {
      console.error('API Error:', apiError);
      setUploadError(apiError.message || 'Failed to upload GL transactions');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle template selection change
  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    setCustomMappings({}); // Reset custom mappings when template changes
  };

  // Get available template fields for mapping
  const getAvailableTemplateFields = (currentExcelField, currentMappedTemplateField) => {
    if (!selectedTemplate) return [];

    const template = templates.find(t => t.id === selectedTemplate);
    if (!template) return [];

    const allTemplateFields = Object.keys(template.mappings || {})
      .filter(field => {
        const mapping = template.mappings[field];
        return mapping && mapping.isActive !== false;
      });

    // Get currently used template fields from mappings
    const usedFields = new Set();
    Object.entries(customMappings).forEach(([excelField, templateField]) => {
      if (excelField !== currentExcelField) {
        usedFields.add(templateField);
      }
    });

    // Return all fields that are either unused or currently selected
    return allTemplateFields
      .filter(field => !usedFields.has(field) || field === currentMappedTemplateField)
      .sort((a, b) => {
        // Sort required fields first, then alphabetically
        const aRequired = template.mappings[a]?.isRequired ? 0 : 1;
        const bRequired = template.mappings[b]?.isRequired ? 0 : 1;

        if (aRequired !== bRequired) {
          return aRequired - bRequired;
        }

        return a.localeCompare(b);
      });
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
        setUploadFile(file);
        setUploadError(null);
      } else {
        setUploadError('Please upload a CSV or Excel file');
      }
    }
  };

  const handleSaveSnowflakeData = async () => {
    if (!selectedPropertyApi || snowflakeData.length === 0) {
      setUploadError('Please select a Property and load Snowflake data.');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/gl-transactions/bulk`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      // Map Snowflake data to GL transactions bulk API format
      const transactionsData = snowflakeData.map((item) => {
        const debitAmount = item.debit > 0 ? item.debit : null;
        const creditAmount = item.credit > 0 ? item.credit : null;
        const txnDirection = debitAmount > 0 ? 'DEBIT' : 'CREDIT';
        const amount = debitAmount > 0 ? debitAmount : creditAmount;

        // Validation: At least one of debitAmount or creditAmount must be provided
        if (!debitAmount && !creditAmount) {
          console.warn('Skipping record - no debit or credit amount found:', item);
          return null;
        }

        // Validation: Transaction date is required
        if (!item.date) {
          console.warn('Skipping record - no transaction date found:', item);
          return null;
        }

        return {
          id: null,
          glAccountName: item.glDescription || '', // Map glDescription to glAccountName
          transactionDate: item.date, // Use BANK_TRANSACTION_DATE from Snowflake
          referenceNo: item.docNo || '',
          description: item.description || '',
          debitAmount: debitAmount,
          creditAmount: creditAmount,
          createdAt: null,
          updatedAt: null,
          tenantId: tenantId,
          propertyId: selectedPropertyApi,
          intrgrationId: null,
          companyId: null, // Will be set by backend or can be mapped if available
          sourceSystem: 'SNOWFLAKE',
          externalTxnId: (item.glSystemTransactionId ?? item.id)?.toString() || '', // GL_SYSTEM_TRANSACTION_ID
          glAccountNo: null, // Will be set by backend or can be mapped if available
          postingDate: item.date, // Use BANK_TRANSACTION_DATE
          documentDate: item.date, // Use BANK_TRANSACTION_DATE
          periodKey: item.date ? item.date.replace(/-/g, '').substring(0, 6) : null, // Format: YYYYMM
          documentNo: item.docNo || '',
          batchNo: null,
          journalCode: 'GL',
          transactionType: null,
          currency: 'USD',
          amount: amount,
          txnDirection: txnDirection,
          counterpartyName: null, // Will be set by backend or can be mapped if available
          counterpartyId: null,
          reconStatus: 'UNMATCHED',
          reconId: null,
          matchedBankTxnId: null,
          matchConfidence: null,
          matchedBy: null,
          matchedAt: null,
          normalizedRef: null,
          normalizedDesc: null,
          hashKey: null,
          isAdjustment: false,
          adjustmentReason: null,
          approvalStatus: 'POSTED',
          approvedBy: null,
          approvedAt: null
        };
      }).filter(item => item !== null); // Remove null entries

      // Additional validation: Ensure we have valid records to save
      if (transactionsData.length === 0) {
        throw new Error('No valid records found. Each record must have a transaction date and either a debit or credit amount.');
      }

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transactionsData),
      });

      if (!resp.ok) {
        let errorMessage = `Failed to save Snowflake data: ${resp.status} ${resp.statusText}`;

        try {
          const errorData = await resp.json();
          if (errorData.error) {
            errorMessage = errorData.error; // Show the actual error message from API
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (jsonError) {
          // If response is not JSON, try to get it as text
          try {
            const errorText = await resp.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (textError) {
            // Keep the default error message
          }
        }

        throw new Error(errorMessage);
      }

      const result = await resp.json();

      // Refresh the GL transactions data
      await fetchTransactions();

      const selectedProperty = propertyApis.find(p => p.propertyId === selectedPropertyApi);
      const propertyName = selectedProperty ? selectedProperty.propertyName : selectedPropertyApi;
      showSuccess(`Successfully saved ${result.length || transactionsData.length} Snowflake records for ${propertyName}`);
      setUploadDialogOpen(false);
      setSnowflakeData([]);
      setSelectedPropertyApi('');
      setSnowflakeFromDate('');
      setSnowflakeToDate('');

    } catch (apiError) {
      console.error('API Error:', apiError);
      setUploadError(apiError.message || 'Failed to save Snowflake data');
    } finally {
      setUploading(false);
    }
  };

  const handleCsvUpload = async () => {
    if (!uploadFile) {
      setUploadError('Please upload a file.');
      return;
    }

    if (!selectedPropertyApi) {
      setUploadError('Please select a Property before uploading.');
      return;
    }

    // Validate that selectedPropertyApi is a valid UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    console.log('GL Transaction Upload - selectedPropertyApi:', selectedPropertyApi);
    console.log('GL Transaction Upload - propertyApis:', propertyApis);

    if (!uuidRegex.test(selectedPropertyApi)) {
      setUploadError('Invalid property selection. Please select a valid property from the dropdown.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/gl-transactions/bulk`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      let transactionsData = [];

      // If template is selected, use template-based processing
      if (selectedTemplate) {
        const template = templates.find(t => t.id === selectedTemplate);
        if (template) {
          const { fields: excelFields, data: excelData } = await extractFieldsFromSheet(uploadFile);

          // Build field mappings including custom mappings
          const fieldMappings = {};
          const templateFields = [];

          if (template.mappings) {
            Object.entries(template.mappings).forEach(([sourceField, mapping]) => {
              if (mapping && mapping.isActive !== false) {
                const matchedField = excelFields.find(field =>
                  field.toLowerCase() === sourceField.toLowerCase()
                );

                if (matchedField) {
                  fieldMappings[sourceField] = {
                    targetColumn: mapping.targetColumn || sourceField,
                    dataType: mapping.dataType || 'TEXT',
                    isRequired: mapping.isRequired || false,
                    excelField: matchedField
                  };
                  templateFields.push(sourceField);
                }
              }
            });
          }

          // Add custom mappings
          Object.entries(customMappings).forEach(([excelField, templateField]) => {
            if (!fieldMappings[templateField] && excelFields.includes(excelField)) {
              const mapping = template.mappings?.[templateField];
              fieldMappings[templateField] = {
                targetColumn: mapping?.targetColumn || templateField,
                dataType: mapping?.dataType || 'TEXT',
                isRequired: mapping?.isRequired || false,
                excelField: excelField
              };
              if (!templateFields.includes(templateField)) {
                templateFields.push(templateField);
              }
            }
          });

          // Process data using mappings
          transactionsData = excelData.map(row => {
            const mappedRow = {
              tenantId: tenantId,
              propertyId: selectedPropertyApi,
              // ledgerAccountId: '',
              glAccountName: '',
              transactionDate: '',
              debitAmount: null,
              creditAmount: null,
              glAccountNo: '',
              referenceNo: '',
              description: ''
            };

            templateFields.forEach(field => {
              const mapping = fieldMappings[field];
              if (mapping?.excelField && row[mapping.excelField] !== undefined) {
                const value = convertValue(row[mapping.excelField], mapping.dataType);

                // Map to the correct fields based on target column
                switch (mapping.targetColumn) {
                  case 'glAccountName':
                    mappedRow.glAccountName = value || '';
                    break;
                  case 'transactionDate':
                    mappedRow.transactionDate = value || '';
                    break;
                  case 'debitAmount':
                    mappedRow.debitAmount = value || null;
                    break;
                  case 'creditAmount':
                    mappedRow.creditAmount = value || null;
                    break;
                  case 'glAccountNo':
                    mappedRow.glAccountNo = value || '';
                    break;
                  case 'referenceNo':
                    mappedRow.referenceNo = value || '';
                    break;
                  case 'description':
                    mappedRow.description = value || '';
                    break;
                }
              }
            });

            // Add sourceSystem for CSV upload
            mappedRow.sourceSystem = 'CSV';

            return mappedRow;
          }).filter(transaction => {
            // Filter out invalid transactions
            return transaction.glAccountName &&
              transaction.transactionDate &&
              (transaction.debitAmount > 0 || transaction.creditAmount > 0);
          });
        }
      } else {
        // Original CSV processing logic
        const reader = new FileReader();

        reader.onload = async (e) => {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());

          if (lines.length === 0) {
            setUploadError('File is empty');
            setUploading(false);
            return;
          }

          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

          const transactionDateIndex = headers.findIndex(h => h === 'TRANSACTION_DATE');
          const glAccountNameIndex = headers.findIndex(h => h === 'LEDGER_ACCOUNT_NAME');
          const glAccountIndex = headers.findIndex(h => h === 'GL_ACCOUNT');
          const referenceNoIndex = headers.findIndex(h => h === 'REFERENCE_NUMBER' || h === 'REFERENCE_NO');
          const descriptionIndex = headers.findIndex(h => h === 'DESCRIPTION');
          const debitIndex = headers.findIndex(h => h === 'DEBIT');
          const creditIndex = headers.findIndex(h => h === 'CREDIT');

          if (transactionDateIndex === -1 || glAccountNameIndex === -1) {
            setUploadError('Required headers not found. Please ensure your file has exactly these headers: TRANSACTION_DATE, LEDGER_ACCOUNT_NAME, REFERENCE_NUMBER, DESCRIPTION, DEBIT, CREDIT, GL_ACCOUNT');
            setUploading(false);
            return;
          }

          transactionsData = lines.slice(1).map((line) => {
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            const transactionDate = values[transactionDateIndex];
            const debitVal = parseFloat(values[debitIndex]) || 0;
            const creditVal = parseFloat(values[creditIndex]) || 0;
            const glAccountName = values[glAccountNameIndex] || '';

            // Validate required fields
            if (!transactionDate || !glAccountName) {
              return null;
            }

            // Ensure at least one of debitAmount or creditAmount is > 0
            if (debitVal <= 0 && creditVal <= 0) {
              return null;
            }

            // Convert date format from MM/dd/yyyy to yyyy-MM-dd for backend
            let formattedDate = transactionDate;
            try {
              // Handle various date formats and convert to ISO format
              if (transactionDate.includes('/')) {
                const parts = transactionDate.split('/');
                if (parts.length === 3) {
                  const month = parts[0].padStart(2, '0');
                  const day = parts[1].padStart(2, '0');
                  const year = parts[2];
                  formattedDate = `${year}-${month}-${day}`;
                }
              }
            } catch (dateError) {
              console.warn('Date format conversion failed:', transactionDate, dateError);
            }

            // Send required and optional fields
            return {
              glAccountName: glAccountName,
              transactionDate: formattedDate,
              debitAmount: debitVal > 0 ? debitVal : null,
              creditAmount: creditVal > 0 ? creditVal : null,
              tenantId: tenantId,
              propertyId: selectedPropertyApi,
              glAccountNo: values[glAccountIndex] || '',
              referenceNo: values[referenceNoIndex] || '',
              description: values[descriptionIndex] || ''
            };
          }).filter(transaction => transaction !== null);

          if (transactionsData.length === 0) {
            setUploadError('No valid GL transactions found in file.');
            setUploading(false);
            return;
          }

          await processUpload(transactionsData, url, token);
        };

        reader.onerror = () => {
          setUploadError('Failed to read the file');
          setUploading(false);
          setUploadProgress(0);
        };

        reader.readAsText(uploadFile);
        return;
      }

      if (transactionsData.length === 0) {
        setUploadError('No valid GL transactions found in the file.');
        setUploading(false);
        return;
      }

      await processUpload(transactionsData, url, token);

    } catch (err) {
      console.error('Error uploading CSV:', err);
      setUploadError(err.message || 'Failed to upload CSV');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle API ingestion
  const handleApiIngestion = async () => {
    setApiIngestionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/gl-transactions/ingest-from-api`;

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tenantId }),
      });

      if (!resp.ok) {
        // If API doesn't exist yet, simulate success
        if (resp.status === 404) {
          showSuccess('GL transactions ingested from API successfully (mock)');
          await fetchTransactions();
          return;
        }
        throw new Error(`Failed to ingest from API: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (data && data.success) {
        showSuccess('GL transactions ingested from API successfully');
        await fetchTransactions();
      } else {
        throw new Error('API ingestion failed');
      }
    } catch (err) {
      console.error('Error ingesting from API:', err);
      showError(err.message || 'Failed to ingest from API');
    } finally {
      setApiIngestionLoading(false);
    }
  };

  // Open Upload Dialog
  const handleOpenUploadDialog = (e) => {
    e.stopPropagation();
    setUploadDialogOpen(true);
    setUploadError(null);
  };

  // Open Add GL Transaction Dialog
  const handleOpenAddGLTransactionDialog = (e) => {
    if (!canAdd) return;
    e.stopPropagation();
    setAddGLTransactionDialogOpen(true);
    setAddGLTransactionError(null);
  };

  // Open Edit GL Transaction Dialog
  // const handleOpenEditGLTransactionDialog = (e) => {
  //   e.stopPropagation();
  //   if (!selectedTransactionRow) {
  //     showError('Please select a GL transaction to edit');
  //     return;
  //   }
  //   if (selectedTransactionRow.isReadOnly) {
  //     showError('Cannot edit a transaction that is already reconciled');
  //     return;
  //   }
  //   setEditingGLTransaction({ ...selectedTransactionRow });
  //   setEditGLTransactionDialogOpen(true);
  //   setEditGLTransactionError(null);
  // };
  const handleOpenEditGLTransactionDialog = (e) => {
    if (!canEdit) return;
    e.stopPropagation();
    if (!selectedTransactionRow) {
      showError('Please select a GL transaction to edit');
      return;
    }
    if (selectedTransactionRow.isReadOnly) {
      if (selectedTransactionRow.rawData?.approvalStatus === 'APPROVED') {
        showError('Cannot edit a transaction that is already approved');
      } else {
        showError('Cannot edit a transaction that is already reconciled');
      }
      return;
    }

    // Determine amount type and amount from existing data
    const hasDebit = selectedTransactionRow.debit && selectedTransactionRow.debit > 0;
    const hasCredit = selectedTransactionRow.credit && selectedTransactionRow.credit > 0;

    setEditingGLTransaction({
      ...selectedTransactionRow,
      ledgerAccountId: selectedTransactionRow.rawData?.ledgerAccountId ?? selectedTransactionRow.ledgerAccountId ?? '',
      amountType: hasDebit ? 'debit' : 'credit',
      amount: hasDebit ? selectedTransactionRow.debit : selectedTransactionRow.credit
    });
    setEditGLTransactionDialogOpen(true);
    setEditGLTransactionError(null);
  };

  // Open Delete GL Transaction Dialog
  const handleOpenDeleteGLTransactionDialog = (e) => {
    if (!canDelete) return;
    e.stopPropagation();
    if (!selectedTransactionRow) {
      showError('Please select a GL transaction to delete');
      return;
    }
    if (selectedTransactionRow.rawData?.approvalStatus === 'APPROVED') {
      showError('Cannot delete a transaction that is already approved');
      return;
    }

    // Check if the record is mapped
    const reconStatus = selectedTransactionRow.rawData?.reconStatus;
    if (reconStatus === 'AUTO_MATCHED' || reconStatus === 'MANUAL_MATCHED') {
      setDeletingGLTransaction(selectedTransactionRow);
      setDeleteGLTransactionDialogOpen(true);
      setDeleteGLTransactionError('This record is mapped with a Bank Statement, cannot delete');
      return;
    }

    setDeletingGLTransaction(selectedTransactionRow);
    setDeleteGLTransactionDialogOpen(true);
    setDeleteGLTransactionError(null);
  };

  // Handle Add GL Transaction
  // const handleAddGLTransaction = async () => {
  //   if (!newGLTransaction.ledgerAccountId.trim()) {
  //     setAddGLTransactionError('Ledger Account ID is required');
  //     return;
  //   }
  //   if (!newGLTransaction.transactionDate.trim()) {
  //     setAddGLTransactionError('Transaction Date is required');
  //     return;
  //   }
  //   if (!newGLTransaction.debitAmount && !newGLTransaction.creditAmount) {
  //     setAddGLTransactionError('Either Debit or Credit amount is required');
  //     return;
  //   }

  //   setAddGLTransactionLoading(true);
  //   setAddGLTransactionError(null);

  //   try {
  //     const token = localStorage.getItem('token');
  //     const tenantId = localStorage.getItem('tenant_id');
  //     const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
  //     const url = `${baseUrl}conductor/cash-recon/gl-transactions`;

  //     if (!token) throw new Error('No authentication token found');
  //     if (!tenantId) throw new Error('No tenant ID found');

  //     // Prepare the request body according to the API specification
  //     const requestBody = {
  //       ledgerAccountId: newGLTransaction.ledgerAccountId.trim(),
  //       transactionDate: newGLTransaction.transactionDate,
  //       debitAmount: newGLTransaction.debitAmount || null,
  //       creditAmount: newGLTransaction.creditAmount || null,
  //       tenantId: tenantId,
  //       propertyId: newGLTransaction.propertyId || null,
  //       glAccountNo: newGLTransaction.glAccountNo || '',
  //       referenceNo: newGLTransaction.referenceNo || null,
  //       description: newGLTransaction.description || null,
  //       transactionType: newGLTransaction.transactionType || null
  //     };

  //     const resp = await fetch(url, {
  //       method: 'POST',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify(requestBody),
  //     });

  //     if (!resp.ok) {
  //       throw new Error(`Failed to add GL transaction: ${resp.status} ${resp.statusText}`);
  //     }

  //     const data = await resp.json();

  //     // Add the new transaction to the local state with the response data
  //     const newTransaction = {
  //       id: data.id || transactions.length + 1,
  //       transactionDate: data.transactionDate,
  //       glAccount: data.glAccountNo || newGLTransaction.glAccountNo,
  //       referenceNo: data.referenceNo || newGLTransaction.referenceNo,
  //       description: data.description || newGLTransaction.description,
  //       debit: data.debitAmount || 0,
  //       credit: data.creditAmount || 0,
  //       propertyId: data.propertyId || newGLTransaction.propertyId,
  //       propertyGroup: '',
  //       sourceIntegration: 'Manual',
  //       reconciliationStatus: 'Pending',
  //       isReadOnly: false,
  //       ...data
  //     };

  //     setTransactions([...transactions, newTransaction]);
  //     showSuccess('GL Transaction added successfully');
  //     setAddGLTransactionDialogOpen(false);
  //     setNewGLTransaction({
  //       ledgerAccountId: '',
  //       transactionDate: '',
  //       referenceNo: '',
  //       description: '',
  //       debitAmount: null,
  //       creditAmount: null,
  //       propertyId: '',
  //       glAccountNo: '',
  //       sourceSystem: 'Manual'
  //     });
  //   } catch (err) {
  //     console.error('Error adding GL transaction:', err);
  //     setAddGLTransactionError(err.message || 'Failed to add GL transaction');
  //   } finally {
  //     setAddGLTransactionLoading(false);
  //   }
  // };
  const handleAddGLTransaction = async () => {
    if (!newGLTransaction.ledgerAccountId.trim()) {
      setAddGLTransactionError('Ledger Account ID is required');
      return;
    }
    if (!newGLTransaction.transactionDate.trim()) {
      setAddGLTransactionError('Transaction Date is required');
      return;
    }
    if (!newGLTransaction.amount || newGLTransaction.amount <= 0) {
      setAddGLTransactionError('Amount is required and must be greater than 0');
      return;
    }

    setAddGLTransactionLoading(true);
    setAddGLTransactionError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/gl-transactions`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      const { systemType, glHeaderId } = await fetchCashReconLedgerScope({ baseUrl, tenantId, token });

      // Get selected ledger account to send glCode as glAccountNo and glDescription as glAccountName
      const selectedLedgerAccount = ledgerAccounts.find(
        (a) => String(a.ledgerAccountId) === String(newGLTransaction.ledgerAccountId)
      );

      // Prepare the request body with conditional debit/credit
      const requestBody = {
        ledgerAccountId: newGLTransaction.ledgerAccountId.trim(),
        transactionDate: newGLTransaction.transactionDate,
        debitAmount: newGLTransaction.amountType === 'debit' ? newGLTransaction.amount : null,
        creditAmount: newGLTransaction.amountType === 'credit' ? newGLTransaction.amount : null,
        tenantId: tenantId,
        propertyId: newGLTransaction.propertyId || null,
        glAccountNo: selectedLedgerAccount?.glCode ?? newGLTransaction.glAccountNo ?? '',
        glAccountName: selectedLedgerAccount?.glDescription ?? '',
        referenceNo: newGLTransaction.referenceNo || null,
        description: newGLTransaction.description || null,
        transactionType: newGLTransaction.transactionType || null,
        sourceSystem: 'MANUAL',
        systemType,
        glHeaderId,
      };

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!resp.ok) {
        throw new Error(`Failed to add GL transaction: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      // Add the new transaction to the local state
      const newTransaction = {
        id: data.id || transactions.length + 1,
        transactionDate: data.transactionDate,
        glAccount: data.glAccountName || selectedLedgerAccount?.glDescription || '',
        referenceNo: data.referenceNo || newGLTransaction.referenceNo,
        description: data.description || newGLTransaction.description,
        debit: data.debitAmount || 0,
        credit: data.creditAmount || 0,
        propertyId: data.propertyId || newGLTransaction.propertyId,
        propertyGroup: '',
        sourceIntegration: 'MANUAL',
        reconciliationStatus: 'Pending',
        isReadOnly: false,
        ...data
      };

      setTransactions([...transactions, newTransaction]);
      showSuccess('GL Transaction added successfully');
      setAddGLTransactionDialogOpen(false);
      setNewGLTransaction({
        ledgerAccountId: '',
        transactionDate: '',
        referenceNo: '',
        description: '',
        amountType: 'debit',
        amount: null,
        propertyId: '',
        glAccountNo: '',
        transactionType: '',
        sourceSystem: 'Manual'
      });
    } catch (err) {
      console.error('Error adding GL transaction:', err);
      setAddGLTransactionError(err.message || 'Failed to add GL transaction');
    } finally {
      setAddGLTransactionLoading(false);
    }
  };

  // Handle Edit GL Transaction
  // const handleEditGLTransaction = async () => {
  //   if (!editingGLTransaction || !editingGLTransaction.ledgerAccountId?.trim()) {
  //     setEditGLTransactionError('Ledger Account ID is required');
  //     return;
  //   }
  //   if (!editingGLTransaction.transactionDate?.trim()) {
  //     setEditGLTransactionError('Transaction Date is required');
  //     return;
  //   }
  //   if (!editingGLTransaction.debit && !editingGLTransaction.credit) {
  //     setEditGLTransactionError('Either Debit or Credit amount is required');
  //     return;
  //   }

  //   setEditGLTransactionLoading(true);
  //   setEditGLTransactionError(null);

  //   try {
  //     const token = localStorage.getItem('token');
  //     const tenantId = localStorage.getItem('tenant_id');
  //     const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

  //     // Use the id for the URL parameter
  //     const transactionId = editingGLTransaction.id;
  //     const url = `${baseUrl}conductor/cash-recon/gl-transactions/${transactionId}`;

  //     if (!token) throw new Error('No authentication token found');
  //     if (!tenantId) throw new Error('No tenant ID found');

  //     // Prepare the request body according to the API specification
  //     const requestBody = {
  //       ledgerAccountId: editingGLTransaction.ledgerAccountId || editingGLTransaction.rawData?.ledgerAccountId,
  //       transactionDate: editingGLTransaction.transactionDate || editingGLTransaction.rawData?.transactionDate,
  //       debitAmount: editingGLTransaction.debit || null,
  //       creditAmount: editingGLTransaction.credit || null,
  //       tenantId: tenantId,
  //       propertyId: editingGLTransaction.propertyId || editingGLTransaction.rawData?.propertyId,
  //       glAccountNo: editingGLTransaction.glAccount || editingGLTransaction.rawData?.glAccountNo,
  //       referenceNo: editingGLTransaction.referenceNo || editingGLTransaction.rawData?.referenceNo,
  //       description: editingGLTransaction.description || editingGLTransaction.rawData?.description,
  //       transactionType: editingGLTransaction.transactionType || null
  //     };

  //     const resp = await fetch(url, {
  //       method: 'PUT',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify(requestBody),
  //     });

  //     if (!resp.ok) {
  //       throw new Error(`Failed to update GL transaction: ${resp.status} ${resp.statusText}`);
  //     }

  //     const data = await resp.json();

  //     // Update the transaction in the local state with the response data
  //     const updatedTransactions = transactions.map(transaction =>
  //       transaction.id === transactionId
  //         ? {
  //           ...transaction,
  //           id: data.id || transaction.id,
  //           transactionDate: data.transactionDate || editingGLTransaction.transactionDate,
  //           glAccount: data.glAccountNo || editingGLTransaction.glAccount,
  //           referenceNo: data.referenceNo || editingGLTransaction.referenceNo,
  //           description: data.description || editingGLTransaction.description,
  //           debit: data.debitAmount || editingGLTransaction.debit,
  //           credit: data.creditAmount || editingGLTransaction.credit,
  //           propertyId: data.propertyId || editingGLTransaction.propertyId,
  //           ...data
  //         }
  //         : transaction
  //     );

  //     setTransactions(updatedTransactions);
  //     showSuccess('GL Transaction updated successfully');
  //     setEditGLTransactionDialogOpen(false);
  //     setEditingGLTransaction(null);
  //   } catch (err) {
  //     console.error('Error updating GL transaction:', err);
  //     setEditGLTransactionError(err.message || 'Failed to update GL transaction');
  //   } finally {
  //     setEditGLTransactionLoading(false);
  //   }
  // };
  const handleEditGLTransaction = async () => {
    if (!editingGLTransaction || !editingGLTransaction.ledgerAccountId?.trim()) {
      setEditGLTransactionError('Ledger Account ID is required');
      return;
    }
    if (!editingGLTransaction.transactionDate?.trim()) {
      setEditGLTransactionError('Transaction Date is required');
      return;
    }
    if (!editingGLTransaction.amount || editingGLTransaction.amount <= 0) {
      setEditGLTransactionError('Amount is required and must be greater than 0');
      return;
    }

    setEditGLTransactionLoading(true);
    setEditGLTransactionError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      const transactionId = editingGLTransaction.id;
      const url = `${baseUrl}conductor/cash-recon/gl-transactions/${transactionId}`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      const { systemType, glHeaderId } = await fetchCashReconLedgerScope({ baseUrl, tenantId, token });

      // Get selected ledger account to send glCode as glAccountNo and glDescription as glAccountName
      const editLedgerAccountId = editingGLTransaction.ledgerAccountId || editingGLTransaction.rawData?.ledgerAccountId;
      const selectedLedgerAccountForEdit = ledgerAccounts.find(
        (a) => String(a.ledgerAccountId) === String(editLedgerAccountId)
      );

      // Prepare the request body with conditional debit/credit
      const requestBody = {
        ledgerAccountId: editLedgerAccountId,
        transactionDate: editingGLTransaction.transactionDate || editingGLTransaction.rawData?.transactionDate,
        debitAmount: editingGLTransaction.amountType === 'debit' ? editingGLTransaction.amount : null,
        creditAmount: editingGLTransaction.amountType === 'credit' ? editingGLTransaction.amount : null,
        tenantId: tenantId,
        propertyId: editingGLTransaction.propertyId || editingGLTransaction.rawData?.propertyId,
        glAccountNo: selectedLedgerAccountForEdit?.glCode ?? editingGLTransaction.glAccountNo ?? editingGLTransaction.glAccount ?? editingGLTransaction.rawData?.glAccountNo ?? '',
        glAccountName: selectedLedgerAccountForEdit?.glDescription ?? editingGLTransaction.rawData?.glAccountName ?? '',
        referenceNo: editingGLTransaction.referenceNo || editingGLTransaction.rawData?.referenceNo,
        description: editingGLTransaction.description || editingGLTransaction.rawData?.description,
        transactionType: editingGLTransaction.transactionType || null,
        sourceSystem: 'MANUAL',
        systemType,
        glHeaderId,
      };

      const resp = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!resp.ok) {
        throw new Error(`Failed to update GL transaction: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      // Update the transaction in the local state
      const updatedTransactions = transactions.map(transaction =>
        transaction.id === transactionId
          ? {
            ...transaction,
            id: data.id || transaction.id,
            transactionDate: data.transactionDate || editingGLTransaction.transactionDate,
            glAccount: data.glAccountName || selectedLedgerAccountForEdit?.glDescription || editingGLTransaction.glAccount,
            referenceNo: data.referenceNo || editingGLTransaction.referenceNo,
            description: data.description || editingGLTransaction.description,
            debit: data.debitAmount || 0,
            credit: data.creditAmount || 0,
            propertyId: data.propertyId || editingGLTransaction.propertyId,
            ...data
          }
          : transaction
      );

      setTransactions(updatedTransactions);
      showSuccess('GL Transaction updated successfully');
      setEditGLTransactionDialogOpen(false);
      setEditingGLTransaction(null);
    } catch (err) {
      console.error('Error updating GL transaction:', err);
      setEditGLTransactionError(err.message || 'Failed to update GL transaction');
    } finally {
      setEditGLTransactionLoading(false);
    }
  };

  // Handle Delete GL Transaction
  const handleDeleteGLTransaction = async () => {
    if (!deletingGLTransaction) return;

    setDeleteGLTransactionLoading(true);
    setDeleteGLTransactionError(null);

    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      // Use the id for the URL parameter
      const transactionId = deletingGLTransaction.id;
      const url = `${baseUrl}conductor/cash-recon/gl-transactions/${transactionId}`;

      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to delete GL transaction: ${resp.status} ${resp.statusText}`);
      }

      // Remove the deleted transaction from the local state
      const updatedTransactions = transactions.filter(transaction => transaction.id !== transactionId);

      setTransactions(updatedTransactions);
      showSuccess('GL Transaction deleted successfully');
      setDeleteGLTransactionDialogOpen(false);
      setDeletingGLTransaction(null);
    } catch (err) {
      console.error('Error deleting GL transaction:', err);
      setDeleteGLTransactionError(err.message || 'Failed to delete GL transaction');
    } finally {
      setDeleteGLTransactionLoading(false);
    }
  };

  // Helper function to get property name by ID
  const getPropertyNameById = (propertyId) => {
    if (!propertyId) return 'N/A';
    const property = propertyApis.find(p => p.propertyId === propertyId);
    return property ? property.propertyName : propertyId;
  };

  // Columns for GL Transactions Grid
  const transactionColumns = [
    {
      field: 'transactionDate',
      headerName: 'Transaction Date',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'glAccount',
      headerName: 'GL Account',
      flex: 1.5,
      minWidth: 120,
    },
    {
      field: 'referenceNo',
      headerName: 'Reference No',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 150,
    },
    {
      field: 'debit',
      headerName: 'Debit',
      flex: 1,
      minWidth: 100,
      type: 'number',
      valueFormatter: (params) => `$${params.value?.toFixed(2) || '0.00'}`,
    },
    {
      field: 'credit',
      headerName: 'Credit',
      flex: 1,
      minWidth: 100,
      type: 'number',
      valueFormatter: (params) => `$${params.value?.toFixed(2) || '0.00'}`,
    },
    // {
    //   field: 'propertyId',
    //   headerName: 'Property ID',
    //   flex: 1,
    //   minWidth: 100,
    // },
    {
      field: 'propertyId',
      headerName: 'Property Name',
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => getPropertyNameById(params.value),
    },
    {
      field: 'propertyGroup',
      headerName: 'Property Group',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'sourceIntegration',
      headerName: 'Source / Integration',
      flex: 1,
      minWidth: 140,
    },
    // {
    //   field: 'reconciliationStatus',
    //   headerName: 'Reconciliation Status',
    //   flex: 1,
    //   minWidth: 140,
    //   renderCell: (params) => (
    //     <Chip
    //       label={params.value}
    //       color={
    //         params.value === 'AUTO_MATCHED' ? 'success' :
    //           params.value === 'MANUAL_MATCHED' ? 'info' :
    //             params.value === 'ADJUSTED' ? 'warning' :
    //               params.value === 'IGNORED' ? 'default' :
    //                 'error'
    //       }
    //       size="small"
    //     />
    //   ),
    // },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 700,
          maxWidth: '100%',
          bgcolor: ht.drawerBg,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={harmonateDrawerHeaderFlushSx}>
        <Typography variant="h6" sx={{ flex: 1, color: ht.white, fontWeight: 700 }}>
          GL Transaction Management
        </Typography>
        <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={harmonateDrawerBodySx}>
        <Box sx={harmonateDrawerAccordionGroupSx}>
        {/* GL Transactions Accordion */}
        <Accordion
          expanded={expanded === 'transactions'}
          onChange={() => setExpanded(expanded === 'transactions' ? false : 'transactions')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>GL Transactions</Typography>
            <IconButton
              onClick={handleOpenEditGLTransactionDialog}
              size="small"
              disabled={!selectedTransactionRow || selectedTransactionRow?.isReadOnly || !canEdit}
              sx={harmonateDrawerEditIconSx}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleOpenDeleteGLTransactionDialog}
              size="small"
              disabled={!selectedTransactionRow || !canDelete}
              sx={harmonateDrawerDeleteIconSx}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleOpenAddGLTransactionDialog}
              size="small"
              disabled={!canAdd}
              sx={harmonateDrawerAddIconSx}
            >
              <AddIcon fontSize="small" />
            </IconButton>
            
            {/* <IconButton
              onClick={handleOpenUploadDialog}
              size="small"
              color="primary"
            >
              <UploadIcon fontSize="small" />
            </IconButton> */}
          </AccordionSummary>
          <AccordionDetails sx={{
            ...harmonateDrawerAccordionDetailsSx,
            height: 500,
            overflow: 'hidden',
            p: 2,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Filters */}
            <Box sx={{ mb: 2 }}>
              {/* Show Filter Button */}
              <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Filters
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    textTransform: 'none',
                    ...harmonateUploadOutlinedSx,
                    borderColor: showFilters ? ht.green : ht.line,
                    color: showFilters ? ht.green : ht.text,
                    '&:hover': {
                      borderColor: ht.green,
                      backgroundColor: ht.secondaryTab,
                    },
                  }}
                >
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </Button>
              </Box>

              {/* Filter Fields - Only show when showFilters is true */}
              {showFilters && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={dateRangeFilter.start}
                      onChange={(e) => setDateRangeFilter(prev => ({ ...prev, start: e.target.value }))}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="End Date"
                      type="date"
                      value={dateRangeFilter.end}
                      onChange={(e) => setDateRangeFilter(prev => ({ ...prev, end: e.target.value }))}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Property Group</InputLabel>
                      <Select
                        value={propertyGroupFilter}
                        label="Property Group"
                        onChange={(e) => setPropertyGroupFilter(e.target.value)}
                        disabled={transactionsLoading || propertyGroupsLoading}
                      >
                        <MenuItem value="ALL">All</MenuItem>
                        {propertyGroups.map(group => (
                          <MenuItem key={group.propertyGroupId} value={group.propertyGroupId}>
                            {group.groupName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      {/* <InputLabel>Property</InputLabel> */}
                      <Autocomplete
                        fullWidth
                        options={properties}
                        getOptionLabel={(option) => {
                          const code = option.propertyCode || '';
                          const name = option.propertyName || '';
                          return code && name ? `${code} - ${name}` : name;
                        }}
                        value={properties.find(p => p.propertyId === propertyIdFilter) || null}
                        onChange={(event, newValue) => {
                          setPropertyIdFilter(newValue ? newValue.propertyId : 'ALL');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Select Property"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {propertiesLoading ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    params.InputProps.endAdornment
                                  )}
                                </>
                              )
                            }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                        sx={{ fontSize: '14px' }}
                      />
                    </FormControl>
                  </Grid>
                  {/* <Grid item xs={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>GL Account</InputLabel>
                      <Select
                        value={glAccountFilter}
                        label="GL Account"
                        onChange={(e) => setGlAccountFilter(e.target.value)}
                        disabled={transactionsLoading}
                      >
                        <MenuItem value="ALL">All</MenuItem>
                        <MenuItem value="1001-Cash">1001-Cash</MenuItem>
                        <MenuItem value="2001-Accounts Payable">2001-Accounts Payable</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid> */}
                  {/* <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Reconciliation Status</InputLabel>
                      <Select
                        value={reconciliationStatusFilter}
                        label="Reconciliation Status"
                        onChange={(e) => setReconciliationStatusFilter(e.target.value)}
                        disabled={transactionsLoading}
                      >
                        <MenuItem value="ALL">All</MenuItem>
                        <MenuItem value="UNMATCHED">Unmatched</MenuItem>
                        <MenuItem value="AUTO_MATCHED">Auto Matched</MenuItem>
                        <MenuItem value="MANUAL_MATCHED">Manual Matched</MenuItem>
                        <MenuItem value="ADJUSTED">Adjusted</MenuItem>
                        <MenuItem value="IGNORED">Ignored</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid> */}
                </Grid>
              )}
            </Box>

            {transactionsError && (
              <Alert severity="error" sx={{ mb: 1 }}>{transactionsError}</Alert>
            )}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 350 }}>
              <DataGridPro
                rows={transactions}
                columns={transactionColumns}
                columnResizeMode="onEnd"
                resizable
                loading={transactionsLoading}
                sx={{ ...harmonateDataGridSx, border: 'none', flex: 1 }}
                getRowId={(row) => row.id}
                rowHeight={35}
                onRowSelectionModelChange={(newSelection) => {
                  if (newSelection.length > 0) {
                    const selectedRow = transactions.find(row => row.id === newSelection[0]);
                    setSelectedTransactionRow(selectedRow);
                  } else {
                    setSelectedTransactionRow(null);
                  }
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                      <Typography>No GL transactions found.</Typography>
                    </Box>
                  )
                }}
                pagination
                paginationMode="server"
                rowCount={txRowCount}
                paginationModel={txPaginationModel}
                onPaginationModelChange={(model) => {
                  if (model.page === txPaginationModel.page && model.pageSize === txPaginationModel.pageSize) {
                    return;
                  }
                  setTxPaginationModel(model);
                  fetchTransactions(model.page, model.pageSize);
                }}
                pageSizeOptions={[50, 100]}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
        </Box>
      </Box>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Upload GL Transactions
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
          <DialogContentText sx={{ mb: 2, fontSize: '14px' }}>
            Select a Property and upload your GL Transactions data file.
          </DialogContentText>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={activeUploadTab}
              onChange={(e, newValue) => setActiveUploadTab(newValue)}
              aria-label="Upload GL Transactions tabs"
            >
              <Tab label="Excel" {...a11yProps(0)} />
              {/* <Tab label="Refresh Data" {...a11yProps(1)} /> */}
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <CustomTabPanel value={activeUploadTab} index={0}>
            {/* Property Dropdown */}
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              {/* <InputLabel>Property</InputLabel> */}
              {loadingPropertyApis ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Loading Properties...
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  fullWidth
                  options={propertyApis}
                  getOptionLabel={(option) => {
                    const code = option.propertyCode || '';
                    const name = option.propertyName || '';
                    return code && name ? `${code} - ${name}` : name;
                  }}
                  value={propertyApis.find(p => p.propertyId === selectedPropertyApi) || null}
                  onChange={(event, newValue) => {
                    setSelectedPropertyApi(newValue ? newValue.propertyId : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Property"
                      size="small"
                      placeholder="Select Property"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingPropertyApis ? (
                              <CircularProgress size={20} />
                            ) : (
                              params.InputProps.endAdornment
                            )}
                          </>
                        )
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                  sx={{ fontSize: '14px' }}
                />
              )}
            </FormControl>

            {/* Required Headers Information */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: ht.listBg, borderRadius: '4px', border: `1px solid ${ht.line}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#495057' }}>
                Required Headers in your file:
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, mb: 0, '& li': { fontSize: '13px', color: '#495057', lineHeight: 1.7 } }}>
                <li><strong>TRANSACTION_DATE</strong> - The transaction date (mandatory)</li>
                <li><strong>LEDGER_ACCOUNT_NAME</strong> - The ledger account name (mandatory)</li>
                <li><strong>REFERENCE_NUMBER</strong> - The reference number (mandatory)</li>
                <li><strong>DESCRIPTION</strong> - The description (mandatory)</li>
                <li><strong>DEBIT/CREDIT</strong> - The debit/credit amount (mandatory)</li>
                <li><strong>GL_ACCOUNT</strong> - The GL account code (optional)</li>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6c757d', fontSize: '12px' }}>
                Note: The file must be in CSV or Excel format with these exact header names in the first row.
              </Typography>
            </Box>

            {/* File Upload Area */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Upload File
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#000000', mb: 2 }}>
                Add your GL Transactions file here
              </Typography>

              <HiddenInput
                accept=".csv,.xls,.xlsx"
                id="upload-file"
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0] || null;
                  if (file) {
                    setUploadFile(file);
                    setUploadError(null);
                  }
                }}
              />
              <label htmlFor="upload-file">
                <UploadArea
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  sx={{
                    padding: '24px 16px',
                    border: isDragging ? `2px dashed ${ht.green}` : `2px dashed ${ht.line}`,
                    borderRadius: '6px',
                    backgroundColor: isDragging ? ht.secondaryTab : ht.listBg,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: ht.green,
                      backgroundColor: ht.secondaryTab,
                    }
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 32, color: ht.green, mb: 1 }} />
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '13px' }}>
                    {isDragging ? 'Drop your file here' : 'Drop your file or browse'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#000000', fontSize: '11px' }}>
                    Max 10 MB files are allowed
                  </Typography>
                </UploadArea>
              </label>

              {uploadFile && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mt: 2,
                    mb: 2,
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: '#e3f2fd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <InsertDriveFileIcon sx={{ fontSize: 24, color: '#388e3c' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 500,
                        color: '#000000',
                        fontSize: '14px',
                        mb: 0.5
                      }}>
                        {uploadFile.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#000000', fontSize: '12px' }}>
                        {(uploadFile.size / 1024).toFixed(2)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setUploadFile(null);
                      setUploadProgress(0);
                    }}
                    sx={{
                      color: '#000000',
                      '&:hover': { color: '#d32f2f', backgroundColor: '#ffebee' }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Paper>
              )}

              <Typography variant="caption" sx={{ color: '#000000', fontSize: '10px', display: 'block', mt: 1.5, mb: 2 }}>
                Only support CSV and XLS/XLSX
              </Typography>
            </Box>

            {/* Template Dropdown */}
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>Template</InputLabel>
              {loadingTemplates ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Loading Templates...
                  </Typography>
                </Box>
              ) : (
                <Select
                  value={selectedTemplate}
                  label="Template"
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  disabled={loadingTemplates}
                >
                  <MenuItem value="">No template selected</MenuItem>
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>

            {/* Preview Button */}
            {selectedTemplate && uploadFile && (
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handlePreviewClick}
                  disabled={!selectedTemplate || !uploadFile}
                sx={harmonateUploadOutlinedSx}
                >
                  Preview & Map Columns
                </Button>
              </Box>
            )}

            {/* Upload Progress */}
            {uploading && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Uploading... ({uploadProgress}%)
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}
            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadError}
              </Alert>
            )}
          </CustomTabPanel>

          <CustomTabPanel value={activeUploadTab} index={1}>
            {/* Property and Date Filters Row */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                {loadingPropertyApis ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ ml: 1 }}>Loading Properties...</Typography>
                  </Box>
                ) : (
                  <Autocomplete
                    fullWidth
                    options={propertyApis}
                    getOptionLabel={(option) => {
                      const code = option.propertyCode || '';
                      const name = option.propertyName || '';
                      return code && name ? `${code} - ${name}` : name;
                    }}
                    value={propertyApis.find(p => p.propertyId === selectedPropertyApi) || null}
                    onChange={(event, newValue) => {
                      setSelectedPropertyApi(newValue ? newValue.propertyId : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Property"
                        size="small"
                        placeholder="Select Property"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingPropertyApis ? (
                                <CircularProgress size={20} />
                              ) : (
                                params.InputProps.endAdornment
                              )}
                            </>
                          )
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                    sx={{ fontSize: '14px' }}
                  />
                )}
              </Grid>

              <Grid item xs={4}>
                {/* <TextField
                  label="From Date"
                  type="date"
                  value={snowflakeFromDate}
                  onChange={(e) => setSnowflakeFromDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                /> */}
                {/* <TextField
                  label="From Date"
                  type="date"
                  value={snowflakeFromDate}
                  onChange={(e) => setSnowflakeFromDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: snowflakeToDate || undefined,
                    onKeyDown: (e) => e.stopPropagation(),
                    onKeyUp: (e) => e.stopPropagation(),
                    onKeyPress: (e) => e.stopPropagation(),
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                /> */}
                <TextField
                  label="From Date"
                  type="date"
                  value={snowflakeFromDate}
                  onChange={(e) => setSnowflakeFromDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

              </Grid>

              <Grid item xs={4}>
                {/* <TextField
                  label="To Date"
                  type="date"
                  value={snowflakeToDate}
                  onChange={(e) => setSnowflakeToDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                /> */}
                {/* <TextField
                  label="To Date"
                  type="date"
                  value={snowflakeToDate}
                  onChange={(e) => setSnowflakeToDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: snowflakeFromDate || undefined,
                    onKeyDown: (e) => e.stopPropagation()
                  }}
                /> */}
                <TextField
                  label="To Date"
                  type="date"
                  value={snowflakeToDate}
                  onChange={(e) => setSnowflakeToDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>


            {/* Load Button */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={fetchSnowflakeData}
                disabled={!selectedPropertyApi || snowflakeLoading}
                sx={harmonateProceedButtonSx}
              >
                {snowflakeLoading ? (
                  <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
                ) : null}
                Load Data from Snowflake
              </Button>
            </Box>

            {/* Error Message */}
            {(snowflakeError || uploadError) && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => { setSnowflakeError(null); setUploadError(null); }}
              >
                {snowflakeError || uploadError}
              </Alert>
            )}

            {/* Snowflake Data Grid */}
            <Box sx={{ height: 350, display: 'flex', flexDirection: 'column' }}>
              {snowflakeLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading Snowflake data...
                  </Typography>
                </Box>
              ) : snowflakeError ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Alert severity="error" onClose={() => setSnowflakeError(null)}>
                    {snowflakeError}
                  </Alert>
                </Box>
              ) : (
                <DataGridPro
                  rows={snowflakeData}
                  columns={snowflakeData.length > 0 && snowflakeData[0].isApiData ? [
                    {
                      field: 'description',
                      headerName: 'Description',
                      flex: 1,
                      minWidth: 200,
                    },
                    {
                      field: 'date',
                      headerName: 'Date',
                      width: 120,
                    },
                    {
                      field: 'docNo',
                      headerName: 'Doc-No',
                      width: 150,
                      minWidth: 120,
                    },
                    {
                      field: 'debit',
                      headerName: 'Debit',
                      width: 120,
                      type: 'number',
                      valueFormatter: (params) => {
                        const value = params.value || 0;
                        return new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                        }).format(value);
                      },
                    },
                    {
                      field: 'credit',
                      headerName: 'Credit',
                      width: 120,
                      type: 'number',
                      valueFormatter: (params) => {
                        const value = params.value || 0;
                        return new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                        }).format(value);
                      },
                    },
                    {
                      field: 'remarks',
                      headerName: 'Remarks',
                      flex: 1,
                      minWidth: 200,
                    }
                  ] : [
                    {
                      field: 'description',
                      headerName: 'Description',
                      flex: 1,
                      minWidth: 200,
                    },
                    {
                      field: 'date',
                      headerName: 'Date',
                      width: 120,
                    },
                    {
                      field: 'docNo',
                      headerName: 'Doc-No',
                      width: 150,
                      minWidth: 120,
                    },
                    {
                      field: 'debit',
                      headerName: 'Debit',
                      width: 120,
                      type: 'number',
                      valueFormatter: (params) => {
                        const value = params.value || 0;
                        return new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                        }).format(value);
                      },
                    },
                    {
                      field: 'credit',
                      headerName: 'Credit',
                      width: 120,
                      type: 'number',
                      valueFormatter: (params) => {
                        const value = params.value || 0;
                        return new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                        }).format(value);
                      },
                    },
                    {
                      field: 'remarks',
                      headerName: 'Remarks',
                      flex: 1,
                      minWidth: 200,
                    },
                    {
                      field: 'originalData.GL_CODE',
                      headerName: 'GL Code',
                      width: 120,
                    },
                    {
                      field: 'originalData.PROPERTY_NAME',
                      headerName: 'Property Name',
                      width: 150,
                    },
                    {
                      field: 'originalData.LEGAL_NAME',
                      headerName: 'Legal Name',
                      width: 150,
                    },
                    {
                      field: 'originalData.LOCATION_CODE',
                      headerName: 'Location Code',
                      width: 120,
                    },
                    {
                      field: 'originalData.FUND_NAME',
                      headerName: 'Fund Name',
                      width: 120,
                    },
                    {
                      field: 'originalData.IS_CASH_GL',
                      headerName: 'Is Cash GL',
                      width: 100,
                    },
                    {
                      field: 'originalData.PERIOD_START_DATE',
                      headerName: 'Period Start',
                      width: 120,
                    },
                    {
                      field: 'originalData.PERIOD_END_DATE',
                      headerName: 'Period End',
                      width: 120,
                    }
                  ]}
                  rowHeight={40}
                  sx={{
                    border: 'none',
                    flex: 1,
                    '& .MuiDataGrid-row:hover': { bgcolor: '#f5f5f5' },
                    '& .MuiDataGrid-cell': { border: 'none' },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: '#f5f5f5',
                      borderBottom: '1px solid #e0e0e0',
                      fontWeight: 600,
                      fontSize: '14px'
                    },
                    '& .MuiDataGrid-columnHeader': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnSeparator': {
                      display: 'none',
                    },
                  }}
                  getRowId={(row) => row.id}
                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                        <Typography>No Snowflake data found.</Typography>
                      </Box>
                    )
                  }}
                  pagination
                  pageSizeOptions={[10, 25, 50, 100]}
                  pageSize={25}
                />
              )}
            </Box>
          </CustomTabPanel>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              setUploadFile(null);
              setSelectedPropertyApi('');
              setSelectedTemplate('');
              setCustomMappings({});
              setUploadProgress(0);
              setUploadError(null);
              setIsDragging(false);
              setActiveUploadTab(0); // Reset to first tab
              setSnowflakeData([]);
              setSnowflakeError(null);
              setSnowflakeFromDate(''); // Reset Snowflake date fields
              setSnowflakeToDate(''); // Reset Snowflake date fields
            }}
            sx={{ fontSize: '13px' }}
          >
            Cancel
          </Button>
          {activeUploadTab === 0 && (
            <Button
              onClick={() => {
                if (selectedPropertyApi && uploadFile) {
                  handleCsvUpload();
                } else {
                  setUploadError('Please select a Property and upload a file.');
                }
              }}
              variant="contained"
              disabled={!selectedPropertyApi || !uploadFile || uploading}
              sx={harmonateProceedButtonSx}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
          {activeUploadTab === 1 && (
            <Button
              onClick={handleSaveSnowflakeData}
              variant="contained"
              disabled={!selectedPropertyApi || snowflakeData.length === 0 || uploading}
              sx={harmonateProceedButtonSx}
            >
              {uploading ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add GL Transaction Dialog */}
      <Dialog
        open={addGLTransactionDialogOpen}
        onClose={() => setAddGLTransactionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Add GL Transaction
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Property — same pattern as ChartOfAccountsDrawer */}
          {loadingPropertyApis ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Loading Properties...
              </Typography>
            </Box>
          ) : (
            <Autocomplete
              fullWidth
              size="small"
              options={propertyApis}
              getOptionLabel={(option) => {
                const code = option.propertyCode || '';
                const name = option.propertyName || '';
                return code && name ? `${code} - ${name}` : name;
              }}
              value={propertyApis.find(p => p.propertyId === newGLTransaction.propertyId) || null}
              onChange={(event, newValue) => {
                setNewGLTransaction({ ...newGLTransaction, propertyId: newValue ? newValue.propertyId : '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Property"
                  required
                  size="small"
                  placeholder="Select Property"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPropertyApis ? (
                          <CircularProgress size={20} />
                        ) : (
                          params.InputProps.endAdornment
                        )}
                      </>
                    )
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
              sx={{ fontSize: '14px' }}
            />
          )}

          <Autocomplete
            fullWidth
            size="small"
            disabled={ledgerAccountsLoading}
            options={ledgerAccounts}
            getOptionLabel={(option) => option.glAccount || ''}
            value={ledgerAccounts.find(acc => acc.ledgerAccountId === newGLTransaction.ledgerAccountId) || null}
            onChange={(event, newValue) => {
              setNewGLTransaction({
                ...newGLTransaction,
                ledgerAccountId: newValue ? newValue.ledgerAccountId : ''
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ledger Account"
                required
                size="small"
                placeholder="Select Ledger Account"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {ledgerAccountsLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        params.InputProps.endAdornment
                      )}
                    </>
                  )
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.ledgerAccountId === value?.ledgerAccountId}
            sx={{ fontSize: '14px' }}
          />

          <TextField
            label="GL Account No"
            fullWidth
            size="small"
            value={newGLTransaction.glAccountNo}
            onChange={(e) => setNewGLTransaction({ ...newGLTransaction, glAccountNo: e.target.value })}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Transaction Date"
              type="date"
              size="small"
              value={newGLTransaction.transactionDate}
              onChange={(e) => setNewGLTransaction({ ...newGLTransaction, transactionDate: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 140 }}
            />
            <TextField
              label="Reference No"
              size="small"
              value={newGLTransaction.referenceNo}
              onChange={(e) => setNewGLTransaction({ ...newGLTransaction, referenceNo: e.target.value })}
              fullWidth
              sx={{ flex: 1, minWidth: 140 }}
            />
          </Box>

          <TextField
            label="Description"
            size="small"
            value={newGLTransaction.description}
            onChange={(e) => setNewGLTransaction({ ...newGLTransaction, description: e.target.value })}
            fullWidth
            required
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <FormControl fullWidth size="small" sx={{ flex: '0 1 200px', minWidth: 160 }}>
              <InputLabel id="gl-add-amount-type-label">Type</InputLabel>
              <Select
                labelId="gl-add-amount-type-label"
                label="Type"
                value={newGLTransaction.amountType}
                onChange={(e) => setNewGLTransaction({
                  ...newGLTransaction,
                  amountType: e.target.value,
                  amount: null
                })}
                required
              >
                <MenuItem value="debit">Debit</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={newGLTransaction.amountType === 'debit' ? 'Debit Amount' : 'Credit Amount'}
              type="number"
              size="small"
              value={newGLTransaction.amount || ''}
              onChange={(e) => {
                const value = e.target.value ? parseFloat(e.target.value) : null;
                setNewGLTransaction({
                  ...newGLTransaction,
                  amount: value
                });
              }}
              fullWidth
              required
              sx={{ flex: 1, minWidth: 160 }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
              }}
              inputProps={{
                step: '0.01',
                min: 0,
              }}
            />
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel id="gl-add-transaction-type-label">Transaction Type</InputLabel>
            <Select
              labelId="gl-add-transaction-type-label"
              label="Transaction Type"
              value={newGLTransaction.transactionType}
              onChange={(e) => setNewGLTransaction({ ...newGLTransaction, transactionType: e.target.value })}
            >
              <MenuItem value="">
                <em>Select a Transaction Type</em>
              </MenuItem>
              <MenuItem value="PAYMENT">Payment</MenuItem>
              <MenuItem value="RECEIPT">Receipt</MenuItem>
              <MenuItem value="TRANSFER">Transfer</MenuItem>
              <MenuItem value="JOURNAL">Journal</MenuItem>
              <MenuItem value="FEE">Fee</MenuItem>
              <MenuItem value="INTEREST">Interest</MenuItem>
              <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
            </Select>
          </FormControl>
          </Box>

          {/* Error Message */}
          {addGLTransactionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {addGLTransactionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setAddGLTransactionDialogOpen(false)}
            disabled={addGLTransactionLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddGLTransaction}
            variant="contained"
            disabled={addGLTransactionLoading}
              sx={harmonateProceedButtonSx}
          >
            {addGLTransactionLoading ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit GL Transaction Dialog */}
      <Dialog
        open={editGLTransactionDialogOpen}
        onClose={() => setEditGLTransactionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Edit GL Transaction
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {loadingPropertyApis ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Loading Properties...
              </Typography>
            </Box>
          ) : (
            <Autocomplete
              fullWidth
              size="small"
              options={propertyApis}
              getOptionLabel={(option) => {
                const code = option.propertyCode || '';
                const name = option.propertyName || '';
                return code && name ? `${code} - ${name}` : name;
              }}
              value={propertyApis.find(p => p.propertyId === editingGLTransaction?.propertyId) || null}
              onChange={(event, newValue) => {
                setEditingGLTransaction({ ...editingGLTransaction, propertyId: newValue ? newValue.propertyId : '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Property"
                  required
                  size="small"
                  placeholder="Select Property"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPropertyApis ? (
                          <CircularProgress size={20} />
                        ) : (
                          params.InputProps.endAdornment
                        )}
                      </>
                    )
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
              sx={{ fontSize: '14px' }}
            />
          )}

          <Autocomplete
            fullWidth
            size="small"
            disabled={ledgerAccountsLoading}
            options={ledgerAccounts}
            getOptionLabel={(option) => option.glAccount || ''}
            value={ledgerAccounts.find(acc => acc.ledgerAccountId === editingGLTransaction?.ledgerAccountId) || null}
            onChange={(event, newValue) => {
              setEditingGLTransaction({
                ...editingGLTransaction,
                ledgerAccountId: newValue ? newValue.ledgerAccountId : ''
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ledger Account"
                required
                size="small"
                placeholder="Select Ledger Account"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {ledgerAccountsLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        params.InputProps.endAdornment
                      )}
                    </>
                  )
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.ledgerAccountId === value?.ledgerAccountId}
            sx={{ fontSize: '14px' }}
          />

          <TextField
            label="GL Account No"
            size="small"
            value={editingGLTransaction?.glAccountNo || editingGLTransaction?.glAccount || ''}
            onChange={(e) => setEditingGLTransaction({ ...editingGLTransaction, glAccountNo: e.target.value })}
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Transaction Date"
              type="date"
              size="small"
              value={editingGLTransaction?.transactionDate || ''}
              onChange={(e) => setEditingGLTransaction({ ...editingGLTransaction, transactionDate: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 140 }}
            />
            <TextField
              label="Reference No"
              size="small"
              value={editingGLTransaction?.referenceNo || ''}
              onChange={(e) => setEditingGLTransaction({ ...editingGLTransaction, referenceNo: e.target.value })}
              fullWidth
              sx={{ flex: 1, minWidth: 140 }}
            />
          </Box>

          <TextField
            label="Description"
            size="small"
            value={editingGLTransaction?.description || ''}
            onChange={(e) => setEditingGLTransaction({ ...editingGLTransaction, description: e.target.value })}
            fullWidth
            required
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <FormControl fullWidth size="small" sx={{ flex: '0 1 200px', minWidth: 160 }}>
              <InputLabel id="gl-edit-amount-type-label">Type</InputLabel>
              <Select
                labelId="gl-edit-amount-type-label"
                label="Type"
                value={editingGLTransaction?.amountType || 'debit'}
                onChange={(e) => setEditingGLTransaction({
                  ...editingGLTransaction,
                  amountType: e.target.value,
                  amount: null
                })}
                required
              >
                <MenuItem value="debit">Debit</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={editingGLTransaction?.amountType === 'debit' ? 'Debit Amount' : 'Credit Amount'}
              type="number"
              size="small"
              value={editingGLTransaction?.amount || ''}
              onChange={(e) => {
                const value = e.target.value ? parseFloat(e.target.value) : null;
                setEditingGLTransaction({
                  ...editingGLTransaction,
                  amount: value
                });
              }}
              fullWidth
              required
              sx={{ flex: 1, minWidth: 160 }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
              }}
              inputProps={{
                step: '0.01',
                min: 0,
              }}
            />
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel id="gl-edit-transaction-type-label">Transaction Type</InputLabel>
            <Select
              labelId="gl-edit-transaction-type-label"
              label="Transaction Type"
              value={editingGLTransaction?.transactionType || ''}
              onChange={(e) => setEditingGLTransaction({ ...editingGLTransaction, transactionType: e.target.value })}
            >
              <MenuItem value="">
                <em>Select a Transaction Type</em>
              </MenuItem>
              <MenuItem value="PAYMENT">Payment</MenuItem>
              <MenuItem value="RECEIPT">Receipt</MenuItem>
              <MenuItem value="TRANSFER">Transfer</MenuItem>
              <MenuItem value="JOURNAL">Journal</MenuItem>
              <MenuItem value="FEE">Fee</MenuItem>
              <MenuItem value="INTEREST">Interest</MenuItem>
              <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
            </Select>
          </FormControl>
          </Box>
          {/* Error Message */}
          {editGLTransactionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {editGLTransactionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setEditGLTransactionDialogOpen(false)}
            disabled={editGLTransactionLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditGLTransaction}
            variant="contained"
            disabled={editGLTransactionLoading}
              sx={harmonateProceedButtonSx}
          >
            {editGLTransactionLoading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete GL Transaction Dialog */}
      <Dialog open={deleteGLTransactionDialogOpen} onClose={() => setDeleteGLTransactionDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {deleteGLTransactionError ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {deleteGLTransactionError}
            </Alert>
          ) : (
            <Typography>
              Are you sure you want to delete the GL transaction "{deletingGLTransaction?.referenceNo || deletingGLTransaction?.description || 'selected'}"?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteGLTransactionDialogOpen(false)} disabled={deleteGLTransactionLoading}>
            Cancel
          </Button>
          {!deleteGLTransactionError && (
            <Button
              onClick={handleDeleteGLTransaction}
              variant="contained"
              disabled={deleteGLTransactionLoading}
              sx={harmonateAbortButtonSx}
            >
              {deleteGLTransactionLoading ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Preview Dialog for Column Mapping */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Preview & Map Columns - {previewData.templateName}
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              File: <strong>{previewData.fileName}</strong>
            </Typography>

            {/* Column Mapping Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Excel Column</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Template Field</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Data Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Required</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.excelFields.map((excelField) => {
                    const mappedTemplateField = Object.keys(customMappings).find(key => key === excelField && customMappings[key]) ||
                      Object.keys(previewData.mappings).find(key => previewData.mappings[key]?.excelField === excelField) || '';
                    const template = templates.find(t => t.id === selectedTemplate);
                    const mapping = template?.mappings?.[customMappings[excelField] || mappedTemplateField];

                    return (
                      <TableRow key={excelField}>
                        <TableCell>{excelField}</TableCell>
                        <TableCell>
                          <Select
                            value={customMappings[excelField] || mappedTemplateField}
                            onChange={(e) => handleCustomMapping(excelField, e.target.value)}
                            displayEmpty
                            size="small"
                            fullWidth
                            sx={{ fontSize: '13px' }}
                          >
                            <MenuItem value="">
                              <em>Do not map</em>
                            </MenuItem>
                            {getAvailableTemplateFields(excelField, customMappings[excelField] || mappedTemplateField).map((field) => {
                              const fieldMapping = template?.mappings?.[field];
                              return (
                                <MenuItem key={field} value={field} sx={{ fontSize: '13px' }}>
                                  {field} {fieldMapping?.isRequired && ' *'}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </TableCell>
                        <TableCell>
                          {mapping ? (
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {mapping.dataType || 'TEXT'}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#000000' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {mapping?.isRequired ? (
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#d32f2f', fontWeight: 600 }}>
                              Yes
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#000000' }}>
                              No
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Preview Data */}
          {previewData.excelData.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Preview Data (First 5 rows)
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {previewData.templateFields.map((field) => (
                        <TableCell key={field} sx={{ fontWeight: 600, fontSize: '12px' }}>
                          {field} {templates.find(t => t.id === selectedTemplate)?.mappings?.[field]?.isRequired && '*'}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.excelData.slice(0, 5).map((row, index) => (
                      <TableRow key={index}>
                        {previewData.templateFields.map((field) => (
                          <TableCell key={field} sx={{ fontSize: '12px' }}>
                            {row[field] || '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setPreviewOpen(false)}
            sx={{ fontSize: '13px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePreviewApply}
            variant="contained"
              sx={harmonateProceedButtonSx}
          >
            Apply Mappings
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default GLTransactionDrawer;